import type { ReactNode } from 'react';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';
import type {
  LoanReviewOutcomeLabel,
  LoanReviewViewModel,
  ReviewReasonNote,
} from '../../lib/loanReview/mapLoanReviewToViewModel';
import { displayStatusLabel } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { statusChipColor } from './loanReviewChipColors';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';

function outcomeMeta(label: LoanReviewOutcomeLabel): {
  severity: 'success' | 'warning' | 'error' | 'info';
  chipColor: 'success' | 'warning' | 'error' | 'default';
  icon: ReactNode;
  headline: string;
} {
  switch (label) {
    case 'Approved':
      return {
        severity: 'success',
        chipColor: 'success',
        icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
        headline: 'Loan review approved this application.',
      };
    case 'Needs review':
      return {
        severity: 'warning',
        chipColor: 'warning',
        icon: <RateReviewOutlinedIcon fontSize="small" />,
        headline: 'A person needs to review this application before funding.',
      };
    case 'Not eligible':
      return {
        severity: 'error',
        chipColor: 'error',
        icon: <ErrorOutlineRoundedIcon fontSize="small" />,
        headline: 'Not eligible for auto-approval — routed to credit/decline review.',
      };
    case 'Failed':
      return {
        severity: 'error',
        chipColor: 'error',
        icon: <ErrorOutlineRoundedIcon fontSize="small" />,
        headline: 'Loan review could not finish.',
      };
    case 'Pending':
      return {
        severity: 'warning',
        chipColor: 'warning',
        icon: <HourglassTopRoundedIcon fontSize="small" />,
        headline: 'Loan review is in progress.',
      };
    default:
      return {
        severity: 'info',
        chipColor: 'default',
        icon: <HelpOutlineRoundedIcon fontSize="small" />,
        headline: `Loan review: ${label}.`,
      };
  }
}

function capitalizeNote(note: string): string {
  const t = note.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function NoteList({
  title,
  notes,
  onJump,
}: {
  title: string;
  notes: ReviewReasonNote[];
  onJump?: (note: ReviewReasonNote) => void;
}) {
  if (!notes.length) return null;
  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.35, fontWeight: 600 }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {notes.map((note) => {
          const label = capitalizeNote(note.text);
          const looksLikeTabLink = /see (employer|income|identity) tab/i.test(note.text);
          const clickable = Boolean(
            onJump && (note.hint || note.targetField || note.appTab || looksLikeTabLink),
          );
          const body = clickable ? (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline={note.appTab || looksLikeTabLink ? 'always' : 'hover'}
              onClick={() => onJump?.(note)}
              sx={{
                textAlign: 'left',
                verticalAlign: 'baseline',
                cursor: 'pointer',
                color: 'inherit',
                font: 'inherit',
                lineHeight: 1.45,
              }}
            >
              {label}
            </Link>
          ) : (
            <Typography component="span" variant="body2" sx={{ lineHeight: 1.45 }}>
              {label}
            </Typography>
          );
          return (
            <Box
              key={note.text}
              component="li"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, lineHeight: 1.45 }}
            >
              {note.hint ? (
                <Tooltip arrow title={note.hint}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.5, maxWidth: '100%' }}>
                    {body}
                    <InfoOutlinedIcon
                      sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', mt: 0.35, flexShrink: 0 }}
                      aria-label={note.hint}
                    />
                  </Box>
                </Tooltip>
              ) : (
                body
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

type HeroProps = {
  vm: LoanReviewViewModel;
  applicationStatus?: string;
  manualReviewHold?: {
    reason: 'EMPLOYMENT_PENDING' | 'AGENT_REVIEW' | 'EMPLOYEE_CONFIRMATION';
    detail?: string | null;
  } | null;
  onJumpToCrossCheck?: (targetField?: string | null) => void;
  onJumpNote?: (note: ReviewReasonNote) => void;
  onJumpToAppTab?: (tab: 'employer' | 'identity' | 'income') => void;
};

export function LoanReviewHero({
  vm,
  applicationStatus,
  manualReviewHold,
  onJumpToCrossCheck,
  onJumpNote,
  onJumpToAppTab,
}: HeroProps) {
  const handleNoteJump = onJumpNote;
  const meta = outcomeMeta(vm.outcomeLabel);
  const summary = vm.businessSummary?.trim() || null;
  const body = summary || vm.primaryReasonPlain?.trim() || null;
  const reviewNotes = vm.reviewReasonNotes ?? [];
  const softNotes = vm.softFlagNotes ?? [];
  const alsoNoted = vm.alsoNotedNotes ?? [];
  const showReviewNotes =
    (vm.outcomeLabel === 'Needs review' || vm.outcomeLabel === 'Not eligible') && reviewNotes.length > 0;
  const isApproved = vm.outcomeLabel === 'Approved';
  const statusUpper = applicationStatus?.trim().toUpperCase() ?? '';
  const showApprovedButHeldAlert =
    isApproved &&
    statusUpper === 'MANUAL_REVIEW' &&
    (manualReviewHold?.reason === 'EMPLOYMENT_PENDING' || manualReviewHold?.reason === 'EMPLOYEE_CONFIRMATION');

  return (
    <ApplicationSectionCard
      title="What's happening"
      icon={meta.icon}
      data-testid="loan-review-hero"
      endAdornment={
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" color={meta.chipColor} label={vm.outcomeLabel} sx={{ fontWeight: 700, height: 22 }} />
          {vm.reviewQueue ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`Queue: ${vm.reviewQueue}`}
              sx={{ fontWeight: 700, height: 22 }}
            />
          ) : null}
        </Stack>
      }
    >
      {showApprovedButHeldAlert ? (
        <Alert severity="info" sx={{ borderRadius: 1.5, mb: 1.25 }} data-testid="loan-review-approved-held-alert">
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            {manualReviewHold?.detail?.trim() ||
              (manualReviewHold?.reason === 'EMPLOYEE_CONFIRMATION'
                ? 'The agent approved this loan, but an employee must confirm before funding.'
                : 'The agent approved this loan, but application status stays in manual review until employment verification clears. See the Manual review tab.')}
          </Typography>
        </Alert>
      ) : null}
      {vm.serviceChips.length ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {vm.serviceChips.map((chip) => {
            const status = chip.status.toUpperCase();
            const notRun = status === 'NOT_RUN';
            const hasData = Boolean(chip.status) && !notRun;
            const serviceSection = vm.services.find((s) => s.key === chip.key);
            const tooltipReasons = serviceSection?.tooltipReasons ?? [];
            const onClick =
              chip.key === 'cross_check' && onJumpToCrossCheck
                ? () => onJumpToCrossCheck('Legal name')
                : chip.key === 'employer' && onJumpToAppTab
                  ? () => onJumpToAppTab('employer')
                  : chip.key === 'identity' && onJumpToAppTab
                    ? () => onJumpToAppTab('identity')
                    : chip.key === 'income' && onJumpToAppTab
                      ? () => onJumpToAppTab('income')
                      : undefined;
            const chipEl = (
              <Chip
                size="small"
                variant="outlined"
                color={hasData ? statusChipColor(chip.status) : 'default'}
                label={`${chip.label}: ${hasData ? displayStatusLabel(chip.status) : notRun ? 'Not run' : 'No data'}`}
                onClick={onClick}
                sx={{
                  fontWeight: 700,
                  height: 22,
                  cursor: onClick ? 'pointer' : undefined,
                }}
              />
            );
            if (!tooltipReasons.length) {
              return <span key={chip.key}>{chipEl}</span>;
            }
            return (
              <Tooltip
                key={chip.key}
                arrow
                title={
                  <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-line' }}>
                    {tooltipReasons.join('\n')}
                  </Box>
                }
              >
                <span>{chipEl}</span>
              </Tooltip>
            );
          })}
        </Stack>
      ) : null}
      <Alert severity={meta.severity} sx={{ borderRadius: 1.5, py: 0.75 }} icon={false}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.35, mb: body ? 0.5 : 0 }}>
          {meta.headline}
        </Typography>
        {body ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {body}
          </Typography>
        ) : null}
        {showReviewNotes ? (
          <NoteList title="Reasons for review" notes={reviewNotes} onJump={handleNoteJump} />
        ) : null}
        {alsoNoted.length > 0 ? (
          <NoteList
            title={
              isApproved
                ? 'Also noted (did not block approval)'
                : 'Also noted (did not block auto-approval on their own)'
            }
            notes={alsoNoted}
            onJump={handleNoteJump}
          />
        ) : null}
        <NoteList
          title={isApproved ? 'Soft differences that did not block approval' : 'Soft differences'}
          notes={softNotes}
          onJump={handleNoteJump}
        />
      </Alert>
    </ApplicationSectionCard>
  );
}
